import { apiClient } from 'axios-client';
import { createPublishedReview } from 'generated/api-client';
import {
  ProReviewInsertInput,
  useProReviewFormCreateQuery,
  useProReviewInvitationCreatedUpdateMutation,
} from 'generated/graphql';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import React, { FunctionComponent, useCallback } from 'react';
import { ToastError, ToastSuccess } from 'utils';
import removeProperties from 'utils/remove-properties';
import sendEvent from 'utils/send-event';
import { ProReviewFormCreateContainer } from './pro-review-form-create-container';

type Props = {
  proPresentationId: string;
};

export type ProReviewCreateInput = ProReviewInsertInput & {
  shouldInformPi?: boolean;
  shouldInformPro?: boolean;
};

type CreateAdminProReviewResponse = {
  id: number;
  proPresentation?: {
    name: string;
    mainTradeNames?: string | null;
  };
};

export const ProReviewFormCreateRepository: FunctionComponent<Props> = ({
  proPresentationId,
}) => {
  const [proReviewInvitationCreatedUpdateMutation] =
    useProReviewInvitationCreatedUpdateMutation();
  const { data, loading } = useProReviewFormCreateQuery({
    variables: {
      proPresentationId: Number(proPresentationId),
    },
  });

  const { currentBilikPerson } = useCurrentBilikPerson();

  const updateRelatedProReviewInvitation = useCallback(
    async (
      proReviewId: number,
      email: string,
      dateOfWork: string | undefined,
    ) => {
      try {
        await proReviewInvitationCreatedUpdateMutation({
          variables: {
            proReviewId,
            proPresentationId: Number(proPresentationId),
            email,
            dateOfWork,
          },
        });
      } catch (err) {
        throw Error(
          'Impossible de mettre à jour les proReviewInvitation en lien avec la proReview',
        );
      }
    },
    [proReviewInvitationCreatedUpdateMutation],
  );

  const createProReview = useCallback(
    async (proReview: ProReviewCreateInput) => {
      if (proReview.email === 'NULL' && proReview.shouldInformPi) {
        ToastError(
          'Erreur',
          "Impossible d'envoyer un email au particulier sans email (valeur NULL).",
        );
        throw Error('Cannot send email');
      }

      const insertProReview = removeProperties(
        proReview,
        'shouldInformPi',
        'shouldInformPro',
      );

      const proPresentationId = data?.proPresentationByPk?.id;

      if (!proPresentationId) {
        ToastError(
          'Erreur',
          "Impossible d'identifier la présentation liée à l'avis.",
        );
        throw Error('Cannot resolve pro presentation');
      }

      const createdProReviewResponse =
        await apiClient.post<CreateAdminProReviewResponse>(
          '/form/review-pro/admin/create',
          {
            proPresentationId,
            ...insertProReview,
          },
        );

      const createdProReview = createdProReviewResponse.data;

      if (!createdProReview?.id) {
        ToastError(
          'Erreur',
          "Impossible de créer l'avis pro, veuillez réessayer ultérieurement.",
        );
        throw Error('Cannot create pro review');
      }

      // V2
      // For now, this V2 route only emits an event to update the spatial index and trace the moderation event in MongoDB
      // (without creating a new review), but in the future it will replace /form/review-pro/admin/create route [TODO]
      await createPublishedReview({
        body: {
          id: createdProReview.id,
        },
      }).catch(() => {
        ToastError('Erreur', "Impossible de publier l'avis");
        throw Error('Cannot publish pro review');
      });

      if (proReview.email) {
        const dateOfWorkTimestamp = proReview.dateOfWork
          ? proReview.dateOfWork.toISOString()
          : undefined;

        await updateRelatedProReviewInvitation(
          createdProReview.id,
          proReview.email,
          dateOfWorkTimestamp,
        );

        if (proReview.shouldInformPi) {
          await apiClient
            .post('/form/review-pro/send-publish-email', {
              proReviewId: createdProReview.id,
              receiverType: 'pi',
            })
            .then((response) => {
              if (response.data?.success) {
                ToastSuccess('Succès', 'Email envoyé au particulier.');
              }
            })
            .catch((err) => {
              ToastError(
                'Erreur',
                `Impossible d'envoyer l'email au particulier : ${err}`,
              );
            });
        }

        if (proReview.shouldInformPro) {
          await apiClient
            .post('/form/review-pro/send-publish-email', {
              proReviewId: createdProReview.id,
              receiverType: 'pro',
            })
            .then((response) => {
              if (response.data?.success) {
                ToastSuccess('Succès', 'Email envoyé au pro.');
              }
            })
            .catch((err) => {
              ToastError(
                'Erreur',
                `Impossible d'envoyer l'email au pro : ${err}`,
              );
            });
        }
      }

      await sendEvent(
        'FormCreateProReview',
        {
          proReview: {
            ...proReview,
            id: createdProReview.id,
          },
        },
        {
          pi: {
            email: proReview.email,
            telephone: proReview.telephone,
          },
          pro: {
            proPresentation: {
              name: createdProReview.proPresentation?.name,
              mainTrade: {
                name: createdProReview.proPresentation?.mainTradeNames,
              },
            },
          },
        },
        {
          bilik: {
            id: currentBilikPerson?.id,
            email: currentBilikPerson?.email,
            givenName: currentBilikPerson?.givenName,
            familyName: currentBilikPerson?.familyName,
          },
        },
        2,
      );
    },
    [data, currentBilikPerson, updateRelatedProReviewInvitation],
  );

  return (
    <ProReviewFormCreateContainer
      createProReview={createProReview}
      loading={loading}
    />
  );
};
